<template>
    <div class="w-full h-full">
        <FormModal v-if="showFormModal" @cancel="showFormModal = false"
            @confirm="updateAnalytics(); showFormModal = false" title="Edit timeframe"
            subtitle="Edit analytics timeframe" :valid="formStart != '' && formEnd != ''" submitButtonText="Apply">

            <CustomInput v-model="formStart" type="date" label="Start date" />
            <CustomInput v-model="formEnd" type="date" label="End date" />

        </FormModal>
        <Header :title="`Spend explorer - ${selectedCategory}`" icon="Telescope" />

        <Breadcrumbs :text1="'spend-explorer'"
            :link1='`/spend-explorer?start=${this.formStart}&end=${this.formEnd}`' :text2="$route.params.category"
            :link2='`/spend-explorer/${$route.params.category}?start=${this.formStart}&end=${this.formEnd}`'>
        </Breadcrumbs>

        <LoadingPlaceholder
            :loading="!finishedloading"
            :stats="true"
            :charts="false"
            :table="true"
        />

        <SubHeader v-if="finishedloading" :title="`${selectedCategory} - Spend Analysis`">
            <template v-slot:buttons>
                <CustomButton id="createContract" small text="Edit timeframe" type="action" icon="CalendarDays"
                    @click="showFormModal = true" />

            </template>
            <template v-if="analytics != undefined && retailers.length > 0" v-slot:stats>
                <StatBox name="Pecentage share of wallet" icon="Percent">
                    <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                        <NumberMaker
                            :number="((analytics.categorySpend.totalTransactionVolume/analytics.totalSpend.totalTransactionVolume)*100)"
                            :commas="true"
                            :percentage="true"
                            :decimals="2"
                            currency=""
                        />
                    </p>
                </StatBox>
                <StatBox name="Spend" icon="CreditCard">
                    <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                        <NumberMaker
                            :number="Math.abs(analytics.categorySpend.totalTransactionVolume)"
                            :commas="true"
                            :percentage="false"
                            :decimals="2"
                            currency="£"
                        />
                    </p>
                </StatBox>
                <StatBox name="Transactions" icon="CreditCard">
                    <p class="text-xl lg:text-lg xl:text-2xl font-semibold text-gray-800 line-clamp-1">
                        <NumberMaker
                            :number="analytics.categorySpend.transactionCount"
                            :commas="true"
                            :percentage="false"
                            :decimals="0"
                            currency=""
                        />
                    </p>
                </StatBox>
                <StatBox name="Top retailer by spend" :number="retailers[0].retailer.retailerName" currency="" icon="Store" />
            </template>
        </SubHeader>

        <WhiteBox v-if="finishedloading" title="Top retailers"
            :subText="`View the top retailers your customer spends with between ${niceDateTime(startDate)} to ${niceDateTime(endDate)}`">
            <template v-slot:content>
                <EasyDataTable :headers="headersTable1" :items="retailers" :loading="!finishedloading"
                    v-model:server-options="serverOptions" :server-items-length="serverItemsLength" :hide-footer="true"
                    :no-hover="true">
                    <template #loading>
                        <div class="p-4 w-full flex justify-center">
                            <svg class="animate-spin text-gray-400 w-12 h-12" xmlns="http://www.w3.org/2000/svg"
                                fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                                    stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                                </path>
                            </svg>
                        </div>
                    </template>
                    <template #empty-message>
                        <div class="flex justify-center py-4">
                            <div
                                class="flex justify-center items-center text-gray-500 rounded-full bg-gray-50 px-5 py-2 ">
                                <p class="text-sm mr-2">No results</p>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 0 1-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0 1 12 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                                </svg>
                            </div>
                        </div>
                    </template>
                    <template #header-action="header">
                        <div class="w-full text-right">
                            {{ header.text }}
                        </div>
                    </template>

                    <template #item-retailer="retailers">

                        <LogoAndText :logo="retailers.retailer.logo" :text="retailers.retailer.retailerName" />
                    </template>
                    <template #item-transactionVolume="{ transactionVolume }">

                        £{{ Math.abs(transactionVolume).toFixed(2) }}
                    </template>
                    <template #header-transactions="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-transactions="{ transactionCount }">
                            <div class="text-right">
                                <NumberMaker
                                    :number="Math.abs(transactionCount)"
                                    :commas="true"
                                    :decimals="0"
                                    currency=""
                                />  
                            </div>
                        </template>
                        <template #header-spend="header">
                            <div class="w-full text-right">
                                {{ header.text }}
                            </div>
                        </template>
                        <template #item-spend="{ transactionVolume }">
                            <div class="text-right">
                                <NumberMaker
                                    :number="Math.abs(transactionVolume)"
                                    :commas="true"
                                    :decimals="2"
                                    currency="£"
                                />  
                            </div>
                        </template>
                    <template #item-action="retailers">
                        <div class="flex justify-end items-center py-1 gap-1">
                            <CustomButton small text="View Retailer" type="navigate"
                                @click="$router.push(`/retailer/${retailers.retailer.id}?start=${this.formStart}&end=${this.formEnd}`)" />
                        </div>
                    </template>
                </EasyDataTable>
            </template>
        </WhiteBox>
    </div>
</template>

<script setup>
</script>

<script>
import Header from "@/components/Header.vue";
import WhiteBox from "@/components/WhiteBox.vue";
import CustomInput from "@/components/inputs/CustomInput.vue";
import CustomButton from "@/components/inputs/CustomButton.vue";
import FormModal from "@/components/modals/FormModal.vue";
import LogoAndText from "@/components/LogoAndText.vue";
import { DateTime } from 'luxon';
import StatBox from "@/components/StatBox.vue";
import SubHeader from "@/components/SubHeader.vue";
import Breadcrumbs from "@/components/navbars/Breadcrumbs.vue";
import NumberMaker from "@/components/NumberMaker.vue";
import LoadingPlaceholder from "@/components/LoadingPlaceholder.vue";


export default {
    name: "manage-publsihers",
    components: {
        Header,
        WhiteBox,
        CustomInput,
        CustomButton,
        FormModal,
        LogoAndText,
        StatBox,
        SubHeader,
        Breadcrumbs,
        NumberMaker,
        LoadingPlaceholder
    },
    props: {
    },

    data() {
        return {
            serverOptions: {
                page: 1,
                rowsPerPage: 50,
            },
            selectedCategory:this.$route.params.category.replaceAll('-',' ').replace(/^\s*([a-z])/i, (match, firstLetter) => firstLetter.toUpperCase()),
    
            analytics:undefined,

            serverItemsLength: undefined,
            startDate: '',
            endDate: '',
            formStart: '',
            formEnd: '',
            category: "",

            showErrorModal: false,
            showSuccessModal: false,
            showFormModal: false,
            loading: true,
            loadingSearch: false,
            loadingAnalytics: true,
            loadingRetailers: true,
            headersTable1: [
                { text: "Retailer", value: "retailer" },
                { text: "Category", value: "retailer.personalisationCategories" },
                { text: "Transactions", value: "transactions" },
                { text: "Spend", value: "spend" },
                { text: "Action", value: "action" },
            ],
            retailers: [

            ],
        }
    },
    created() {
        if (this.$route.query.start && this.$route.query.end) {
            this.formStart = this.$route.query.start
            this.formEnd = this.$route.query.end
            this.startDate = DateTime.fromFormat(this.formStart,'dd-mm-yyyy').startOf('day');
            this.endDate = DateTime.fromFormat(this.formEnd,'dd-mm-yyyy').endOf('day');


        } else {
            const now = DateTime.local();
            const startOfMonth = now.startOf('month');
            const endOfMonth = now.endOf('month');

            this.startDate = startOfMonth.minus({ month: 2 });
            this.endDate = endOfMonth;
            this.formStart = this.startDate.toFormat('dd-MM-yyyy')
            this.formEnd = this.endDate.toFormat('dd-MM-yyyy')

        }
        this.getRetailers(0, 50)
        this.getAnalytics()

    },
    methods: {

        niceDateTime(dateObject) {
            return DateTime.fromISO(dateObject).toLocaleString(DateTime.DATE_SHORT)
        },

        updateAnalytics() {
            this.startDate = DateTime.fromISO(this.formStart).startOf('day');
            this.endDate = DateTime.fromISO(this.formEnd).endOf('day');
            this.formStart = this.startDate.toFormat('dd-MM-yyyy')
            this.formEnd = this.endDate.toFormat('dd-MM-yyyy')
            this.getAnalytics()
            this.getRetailers(0, 50)

        },

        async getAnalytics() {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')

                this.loadingAnalytics = true
                var category = this.$route.params.category

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/share-of-wallet/category/${category}?startDate=${this.formStart}&endDate=${this.formEnd}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.analytics = object
                    this.loadingAnalytics = false
                }

            } catch (error) {
                return;
            }

        },


        async getRetailers(page, size) {
            try {
                const token = localStorage.getItem('token')
                const api  = localStorage.getItem('apiType')

                this.loadingRetailers = true

                var category = this.selectedCategory.replaceAll(' ','%20')
                if(category == 'Food%20and%20drink')
                {
                    category = 'Food%20and%20Drink';
                }
                if(category == 'Saas%20tools')
                {
                    category = 'SaaS%20tools';

                }

                const response = await this.$apiFetch(`https://${api}.boogi.io/private/api/client/share-of-wallet/retailer/popularity?page=${page}&size=${size}&startDate=${this.formStart}&endDate=${this.formEnd}${category == '' ? '' : '&categories=' + category}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                });

                if (response.ok) {
                    var object = await response.json()
                    this.serverItemsLength = object.length
                    this.retailers = object
                    this.loadingRetailers = false
                }

            } catch (error) {
                return;
            }

        },

    },
    computed: {
        finishedloading() {
            return this.loadingAnalytics == false && this.loadingRetailers == false;
        },
    },
    mounted() {
        document.title = "Dashboard | Spend explorer";
    },
    watch: {
        serverOptions: {
            deep: true,
            handler(val) {
                this.getRetailers(val.page - 1, val.rowsPerPage)
            }
        }
    }
}
</script>